// /plan/engagements — Module 7 library. Mirrors /plan/guidance-profiles:
// scope filter (preset/mine/all), preset rows with clone action, tenant
// rows with delete, warhead-type + fuze-mode + salvo-mode chips.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import { sim } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { presetImmutabilityToast } from "@/lib/presetImmutability";

const SCOPES = [
    { id: "tenant", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

const WARHEAD_LABELS = {
    blast_fragmentation: "Blast-frag",
    kinetic_impact: "Kinetic",
    net_capture: "Net",
    directed_energy: "DE",
};

const FUZE_LABELS = {
    proximity: "Proximity",
    contact: "Contact",
    time_after_launch: "Time",
};

const SALVO_LABELS = {
    single: "Single",
    salvo: "Salvo",
    shoot_look_shoot: "SLS",
    shoot_shoot_look: "SSL",
};

function param(v) {
    return typeof v === "number" ? v : (v && typeof v === "object" ? v.value : null);
}

export default function EngagementsPage() {
    const [items, setItems] = useState([]);
    const [scope, setScope] = useState("tenant");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const list = await sim.engagements.list({ scope });
            setItems(Array.isArray(list) ? list : []);
        } catch (e) {
            setErr(formatApiError(e, "Load failed"));
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        load();
    }, [load]);

    const onClone = async (id) => {
        setBusyId(id);
        try {
            const cloned = await sim.engagements.clone(id);
            toast.success(`Cloned as "${cloned.name}"`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setBusyId(null);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this engagement?")) return;
        setBusyId(id);
        try {
            await sim.engagements.remove(id);
            toast.success("Engagement deleted");
            await load();
        } catch (e) {
            presetImmutabilityToast(e, {
                entityType: "engagements",
                id,
                onCloned: async () => { await load(); },
            });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="engagements-page">
            <PageHeader
                title="Engagements"
                subtitle="Warhead + fuzing + Pk curve + salvo doctrine."
                actions={
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger
                            className="w-[170px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="engagements-scope"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            {SCOPES.map((s) => (
                                <SelectItem
                                    key={s.id}
                                    value={s.id}
                                    className="text-[12px] font-mono"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />
            <div className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3">
                {loading ? (
                    <div className="text-xs text-ux-text-3">Loading…</div>
                ) : err ? (
                    <div className="text-xs text-ux-danger" data-testid="engagements-error">
                        {err}
                    </div>
                ) : items.length === 0 ? (
                    <EmptyState
                        title="No engagements yet"
                        description="Clone a preset to get started, or create a new engagement from the Workbench."
                    />
                ) : (
                    <ul className="space-y-2" data-testid="engagements-list">
                        {items.map((p) => {
                            const lr = param(p?.warhead?.lethal_radius_m);
                            return (
                                <li
                                    key={p.id}
                                    className="flex items-start gap-3 px-3 py-2 rounded-md border border-ux-border-1 hover:border-ux-border-2"
                                    data-testid={`engagement-row-${p.id}`}
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="font-medium">{p.name}</span>
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3 border border-ux-border-1 rounded-full px-2 py-0.5">
                                                {WARHEAD_LABELS[p?.warhead?.type] || p?.warhead?.type}
                                            </span>
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3 border border-ux-border-1 rounded-full px-2 py-0.5">
                                                {FUZE_LABELS[p.fuze_mode] || p.fuze_mode}
                                            </span>
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3 border border-ux-border-1 rounded-full px-2 py-0.5">
                                                {SALVO_LABELS[p?.salvo?.mode] || p?.salvo?.mode}
                                            </span>
                                            {p.is_preset ? (
                                                <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                                    preset
                                                </span>
                                            ) : null}
                                        </div>
                                        {p.notes ? (
                                            <div className="text-[12px] text-ux-text-3 mt-1">{p.notes}</div>
                                        ) : null}
                                        <div className="text-[11px] font-mono text-ux-text-3 mt-1">
                                            Lethal R {lr != null ? `${lr.toFixed(1)} m` : "—"}
                                            {" · Shots "}
                                            {p?.salvo?.shots_total ?? "—"}
                                            {p?.salvo?.mode !== "single" && param(p?.salvo?.shots_spacing_s) != null
                                                ? ` · Δ${param(p.salvo.shots_spacing_s)}s`
                                                : ""}
                                        </div>
                                    </div>
                                    {p.is_preset ? (
                                        <SpinnerButton
                                            size="sm"
                                            variant="ghost"
                                            loading={busyId === p.id}
                                            onClick={() => onClone(p.id)}
                                            testId={`engagement-clone-${p.id}`}
                                            title="Clone to my tenant"
                                            aria-label="Clone to my tenant"
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                        </SpinnerButton>
                                    ) : (
                                        <>
                                            <PresetPublishButton
                                                entityType="engagements"
                                                entityId={p.id}
                                                entity={p}
                                                user={user}
                                                size="sm"
                                                variant="ghost"
                                                label=""
                                                testId={`engagement-publish-${p.id}`}
                                                onPublished={load}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => onDelete(p.id)}
                                                className="text-ux-text-3 hover:text-ux-danger"
                                                data-testid={`engagement-delete-${p.id}`}
                                                aria-label="Delete"
                                                title="Delete"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className="text-[11px] text-ux-text-3">
                <Link to="/plan/guidance-profiles" className="text-ux-accent underline decoration-dotted">← Guidance</Link>
            </div>
        </div>
    );
}
